import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { labTestAPI, patientAPI, doctorAPI } from '../../lib/api';
import { formatDateTime, getStatusColor, formatCurrency, apiErrorMessage } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { FlaskConical, Plus, ClipboardCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import SearchableSelect from '../../components/SearchableSelect/SearchableSelect';
import SuggestItemModal from '../../components/SuggestItem/SuggestItemModal';
import { logger } from '../../lib/logger';

const LAB_CATEGORIES = [
  'Hematology',
  'Biochemistry',
  'Microbiology',
  'Serology',
  'Urinalysis',
  'Pathology',
  'Immunology',
];

const LAB_TESTS = {
  Hematology: ['Complete Blood Count (CBC)', 'Hemoglobin', 'Platelet Count', 'ESR', 'Blood Group'],
  Biochemistry: ['Blood Sugar (Fasting)', 'Blood Sugar (PP)', 'HbA1c', 'Lipid Profile', 'Liver Function Test', 'Kidney Function Test', 'Thyroid Profile'],
  Microbiology: ['Urine Culture', 'Blood Culture', 'Stool Culture', 'Throat Swab'],
  Serology: ['Widal Test', 'VDRL', 'HIV Test', 'Hepatitis B', 'Hepatitis C', 'Dengue NS1'],
  Urinalysis: ['Routine Urine', 'Urine Microscopy', '24-Hour Urine Protein'],
  Pathology: ['Biopsy', 'FNAC', 'Pap Smear'],
  Immunology: ['ANA', 'RA Factor', 'CRP', 'ASO Titre'],
};

const LabTestList = () => {
  const { user, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [labTests, setLabTests] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientHistory, setPatientHistory] = useState([]);
  const [resultData, setResultData] = useState({ result: '', status: 'completed' });
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [currentTestIndex, setCurrentTestIndex] = useState(null);
  
  // Get patient_id from URL
  const queryParams = new URLSearchParams(location.search);
  const patientIdFromUrl = queryParams.get('patient_id');
  
  const [tests, setTests] = useState([
    { test_category: '', test_name: '', urgency: 'normal', notes: '' }
  ]);
  
  const [formData, setFormData] = useState({
    patient_id: patientIdFromUrl || '',
    requested_by: '',
  });

  useEffect(() => {
    if (patientIdFromUrl) {
      fetchPatientHistory(patientIdFromUrl);
      setDialogOpen(true);
    }
  }, [patientIdFromUrl]);

  const fetchData = useCallback(async () => {
    try {
      const params = patientIdFromUrl ? { patient_id: patientIdFromUrl } : {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const [testRes, patRes, docRes] = await Promise.all([
        labTestAPI.getAll(params),
        patientAPI.getAll({}),
        doctorAPI.getAll(),
      ]);
      
      // Handle paginated response structures
      setLabTests(testRes.data.lab_tests || testRes.data || []);
      setPatients(patRes.data.patients || patRes.data || []);
      setDoctors(docRes.data.doctors || docRes.data || []);
      
      // Set selected patient if from URL
      if (patientIdFromUrl) {
        const patientsArray = patRes.data.patients || patRes.data || [];
        const patient = patientsArray.find(p => p.id === patientIdFromUrl);
        setSelectedPatient(patient);
      }
    } catch (error) {
      logger.error('Error loading lab test data:', error);
      toast.error(apiErrorMessage(error, 'Failed to load lab test data. Please try again.'));
      // Set empty arrays to prevent crashes
      setLabTests([]);
      setPatients([]);
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, patientIdFromUrl]);


  useEffect(() => {
    fetchData();
  }, [statusFilter, fetchData]);
  const fetchPatientHistory = async (patientId) => {
    try {
      const response = await labTestAPI.getAll({ patient_id: patientId });
      setPatientHistory(response.data.lab_tests || response.data || []);
    } catch (error) {
      logger.error('Failed to load patient history:', error);
      setPatientHistory([]);
    }
  };

  const addTest = () => {
    setTests([...tests, { test_category: '', test_name: '', urgency: 'normal', notes: '' }]);
  };

  const removeTest = (index) => {
    if (tests.length > 1) {
      setTests(tests.filter((_, i) => i !== index));
    }
  };

  const updateTest = (index, field, value) => {
    const newTests = [...tests];
    newTests[index][field] = value;
    setTests(newTests);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Submit each test
      for (const test of tests) {
        const data = {
          patient_id: formData.patient_id,
          test_category: test.test_category,
          test_name: test.test_name,
          urgency: test.urgency,
          notes: test.notes,
          requested_by: user.id,
          price: 0,
        };
        await labTestAPI.create(data);
      }
      
      toast.success(`${tests.length} lab test(s) ordered successfully`);
      setDialogOpen(false);
      
      // Navigate back to patient detail page
      if (patientIdFromUrl) {
        navigate(`/patients/${patientIdFromUrl}`);
      } else {
        setTests([{ test_category: '', test_name: '', urgency: 'normal', notes: '' }]);
        setFormData({ patient_id: '', requested_by: '' });
        fetchData();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (Array.isArray(error.response.data.detail) 
            ? error.response.data.detail.map(e => e.msg || e).join(', ')
            : error.response.data.detail)
        : 'Failed to create lab test';
      toast.error(errorMsg);
    }
  };

  const openResultDialog = (test) => {
    setSelectedTest(test);
    setResultData({ result: test.result || '', status: 'completed' });
    setResultDialogOpen(true);
  };

  const handleUpdateResult = async () => {
    try {
      await labTestAPI.update(selectedTest.id, resultData);
      toast.success('Test result updated');
      setResultDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update result');
    }
  };

  return (
    <div data-testid="lab-tests-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Laboratory Tests</h1>
            <p className="text-slate-500">Manage lab test orders and results</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open && patientIdFromUrl) {
              navigate(`/patients/${patientIdFromUrl}`);
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-red-500 hover:bg-red-600 rounded-full" data-testid="new-lab-test-btn">
                <Plus className="w-4 h-4 mr-2" />
                Order Lab Test
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-[Manrope]">
                  {selectedPatient ? `Order Lab Tests for ${selectedPatient.full_name}` : 'Order Lab Tests'}
                </DialogTitle>
              </DialogHeader>

              {/* Patient History */}
              {selectedPatient && patientHistory.length > 0 && (
                <Card className="mt-4">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold">Previous Lab Tests</CardTitle>
                  </CardHeader>
                  <CardContent className="max-h-32 overflow-y-auto">
                    <div className="space-y-2">
                      {patientHistory.slice(0, 3).map((test) => (
                        <div key={test.id} className="p-2 bg-slate-50 rounded-md text-xs">
                          <div className="flex justify-between">
                            <span className="font-medium">{test.test_name}</span>
                            <Badge className={test.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                              {test.status}
                            </Badge>
                          </div>
                          <p className="text-slate-500">{formatDateTime(test.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {!patientIdFromUrl && (
                  <div>
                    <Label>Patient *</Label>
                    <Select value={formData.patient_id} onValueChange={(v) => { setFormData({...formData, patient_id: v}); fetchPatientHistory(v); }}>
                      <SelectTrigger data-testid="select-patient">
                        <SelectValue placeholder="Select patient" />
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={5}>
                        {patients.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.full_name} ({p.patient_id})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {patientIdFromUrl && selectedPatient && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <strong>Patient:</strong> {selectedPatient.full_name} ({selectedPatient.patient_id})
                    </p>
                  </div>
                )}

                {/* Multiple Tests */}
                <div className="space-y-4">
                  <Label className="text-base font-semibold">Lab Tests to Order:</Label>
                  {tests.map((test, index) => (
                    <Card key={test.id} className="p-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium">Test #{index + 1}</span>
                          {tests.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeTest(index)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Category *</Label>
                            <Select value={test.test_category} onValueChange={(v) => updateTest(index, 'test_category', v)}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent position="popper" sideOffset={5}>
                                {LAB_CATEGORIES.map(cat => (
                                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label>Test Name *</Label>
                            <SearchableSelect
                              value={test.test_name}
                              onValueChange={(v) => updateTest(index, 'test_name', v)}
                              onSuggestNew={(query) => {
                                setCurrentTestIndex(index);
                                setShowSuggestModal(true);
                              }}
                              searchEndpoint={`${process.env.REACT_APP_BACKEND_URL}/api/lab-tests/search`}
                              placeholder="Search lab test..."
                              searchPlaceholder="Type to search tests..."
                              emptyMessage="No lab tests found"
                              disabled={!test.test_category}
                            />
                          </div>
                        </div>

                        <div>
                          <Label>Urgency</Label>
                          <Select value={test.urgency} onValueChange={(v) => updateTest(index, 'urgency', v)}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent position="popper" sideOffset={5}>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="urgent">Urgent</SelectItem>
                              <SelectItem value="stat">STAT</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Clinical Notes</Label>
                          <Textarea
                            value={test.notes}
                            onChange={(e) => updateTest(index, 'notes', e.target.value)}
                            placeholder="Clinical indication, symptoms..."
                            rows={2}
                          />
                        </div>
                      </div>
                    </Card>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={addTest}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Another Test
                  </Button>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setDialogOpen(false);
                      if (patientIdFromUrl) {
                        navigate(`/patients/${patientIdFromUrl}`);
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="submit-lab-test">
                    Order {tests.length} Test{tests.length > 1 ? 's' : ''}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="page-content">
        <Card>
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-red-500" />
                Lab Test Orders
              </CardTitle>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading lab tests...</div>
            ) : labTests.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No lab tests found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Test ID</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Test</TableHead>
                    <TableHead>Urgency</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Ordered</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {labTests.map((test) => (
                    <TableRow key={test.id} data-testid={`lab-test-row-${test.id}`}>
                      <TableCell className="font-mono text-sm">{test.test_id}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{test.patient?.full_name}</p>
                          <p className="text-xs text-slate-500">{test.patient?.patient_id}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{test.test_name}</p>
                          <p className="text-xs text-slate-500">{test.test_category}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={test.urgency === 'stat' ? 'bg-red-100 text-red-800' : test.urgency === 'urgent' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-800'}>
                          {test.urgency?.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatCurrency(test.price)}</TableCell>
                      <TableCell>{formatDateTime(test.created_at)}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(test.status)}>{test.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {hasRole(['super_admin', 'admin', 'lab_technician']) && test.status !== 'completed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openResultDialog(test)}
                            data-testid={`update-${test.id}`}
                          >
                            <ClipboardCheck className="w-4 h-4 mr-1" />
                            Update
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Result Dialog */}
      <Dialog open={resultDialogOpen} onOpenChange={setResultDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Update Test Result</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Test</Label>
              <p className="font-medium">{selectedTest?.test_name}</p>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={resultData.status} onValueChange={(v) => setResultData({...resultData, status: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Result</Label>
              <Textarea
                value={resultData.result}
                onChange={(e) => setResultData({...resultData, result: e.target.value})}
                placeholder="Enter test results"
                rows={4}
                data-testid="input-result"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResultDialogOpen(false)}>Cancel</Button>
              <Button className="bg-red-500 hover:bg-red-600" onClick={handleUpdateResult} data-testid="save-result">Save Result</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Suggest New Lab Test Modal */}
      <SuggestItemModal
        open={showSuggestModal}
        onClose={() => setShowSuggestModal(false)}
        itemType="lab_test"
        onSuggestionCreated={() => {
          toast.info('Your suggestion has been submitted for approval');
        }}
      />
    </div>
  );
};

export default LabTestList;
